import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "@/lib/api";

const fmtDay = (d) => `${d.slice(8, 10)}.${d.slice(5, 7)}`;
const fmtPln = (n) => `${Number(n || 0).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;
const RANGES = [[30, "30 dni"], [90, "90 dni"], [365, "12 miesięcy"]];

function Kpi({ label, value, sub, testId }) {
  return (
    <div data-testid={testId} className="bg-card border border-border rounded-2xl p-5">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
      <p className="font-display font-bold text-2xl sm:text-3xl mt-2 leading-none">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-2">{sub}</p>}
    </div>
  );
}

const tooltipStyle = { contentStyle: { borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 } };

export function StatsAdmin() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/admin/stats", { params: { days } }).then((r) => setData(r.data)).catch(() => toast.error("Nie udało się pobrać statystyk."));
  }, [days]);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-600" /></div>;
  const t = data.totals;
  const series = data.series.map((s) => ({ ...s, day: fmtDay(s.date) }));
  const conv = t.orders ? Math.round((t.paid / t.orders) * 100) : 0;

  return (
    <div data-testid="stats-admin" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-lg">Statystyki</h2>
          <p className="text-sm text-muted-foreground">Zamówienia i płatności od {fmtDay(data.since)} · łącznie w bazie: {data.all_time_orders} zamówień</p>
        </div>
        <div className="flex gap-2">
          {RANGES.map(([d, l]) => (
            <button key={d} type="button" data-testid={`stats-range-${d}`} onClick={() => setDays(d)}
              className={`text-xs font-semibold rounded-full px-4 py-2 border transition-colors ${days === d ? "bg-brand-navy text-white border-brand-navy dark:bg-white dark:text-slate-900" : "border-border hover:bg-muted"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testId="stats-kpi-orders" label="Zamówienia" value={t.orders} sub={`${t.pending} w trakcie płatności · ${t.unpaid} nieopłaconych`} />
        <Kpi testId="stats-kpi-paid" label="Opłacone online" value={t.paid} sub={`${conv}% zamówień opłaconych przez PayU`} />
        <Kpi testId="stats-kpi-revenue" label="Wpłaty (brutto)" value={fmtPln(t.revenue)} sub="wg proform zamówień opłaconych" />
        <Kpi testId="stats-kpi-top" label="Najpopularniejszy pakiet" value={data.top_package || "—"} sub={data.packages[0] ? `${data.packages[0].count} z ${t.orders} zamówień` : ""} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div data-testid="stats-chart-orders" className="lg:col-span-2 bg-card border border-border rounded-2xl p-5">
          <p className="font-semibold text-sm mb-4">Zamówienia i płatności dziennie</p>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={series} margin={{ left: -20, right: 8 }}>
              <defs>
                <linearGradient id="gOrders" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1B2432" stopOpacity={0.35} /><stop offset="100%" stopColor="#1B2432" stopOpacity={0} /></linearGradient>
                <linearGradient id="gPaid" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#148F86" stopOpacity={0.5} /><stop offset="100%" stopColor="#148F86" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.08} />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={days > 90 ? 30 : days > 30 ? 8 : 3} tickLine={false} axisLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip {...tooltipStyle} formatter={(v, n) => [v, n === "orders" ? "Zamówienia" : "Opłacone"]} labelFormatter={(l) => `Dzień ${l}`} />
              <Area type="monotone" dataKey="orders" name="orders" stroke="#1B2432" strokeWidth={2} fill="url(#gOrders)" />
              <Area type="monotone" dataKey="paid" name="paid" stroke="#148F86" strokeWidth={2} fill="url(#gPaid)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div data-testid="stats-chart-packages" className="bg-card border border-border rounded-2xl p-5">
          <p className="font-semibold text-sm mb-4">Pakiety w zamówieniach</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.packages} layout="vertical" margin={{ left: 8, right: 16 }}>
              <XAxis type="number" hide allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip {...tooltipStyle} formatter={(v) => [v, "Zamówień"]} />
              <Bar dataKey="count" fill="#F4B942" radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div data-testid="stats-chart-revenue" className="bg-card border border-border rounded-2xl p-5">
        <p className="font-semibold text-sm mb-4">Wpłaty dziennie (brutto)</p>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={series} margin={{ left: -10, right: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.08} vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={days > 90 ? 30 : days > 30 ? 8 : 3} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
            <Tooltip {...tooltipStyle} formatter={(v) => [fmtPln(v), "Wpłaty"]} labelFormatter={(l) => `Dzień ${l}`} />
            <Bar dataKey="revenue" fill="#148F86" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <p className="text-[11px] text-muted-foreground mt-3">Liczone z zamówień opłaconych przez PayU (status „Opłacone”) wg daty zaksięgowania. Wiadomości z formularza w okresie: {t.messages} · przesłane dokumenty: {t.uploads}.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <SourceTable testId="stats-sources" title="Skąd klienci wchodzą do kreatora" sub="Wejścia do kreatora zamówienia wg miejsca kliknięcia (cennik, tabela, quiz, czat, linki w artykułach) i ile z nich skończyło się zamówieniem." rows={data.sources || []} empty="Brak wejść do kreatora w tym okresie." />
        <SourceTable testId="stats-articles" title="Artykuły, które sprzedają" sub="Wejścia do kreatora z linków i przycisków w artykułach bazy wiedzy oraz zamówienia i wpłaty z nich." rows={data.articles || []} empty="Żadne wejście z artykułu w tym okresie. Wstaw w artykule [[PRZYCISK]] lub link do kreatora." article />
      </div>
    </div>
  );
}

function SourceTable({ testId, title, sub, rows, empty, article = false }) {
  return (
    <div data-testid={testId} className="bg-card border border-border rounded-2xl p-5">
      <p className="font-semibold text-sm">{title}</p>
      <p className="text-[11px] text-muted-foreground mt-1 mb-4">{sub}</p>
      {rows.length === 0 ? (
        <p data-testid={`${testId}-empty`} className="text-sm text-muted-foreground py-6 text-center">{empty}</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
              <th className="text-left font-semibold pb-2">{article ? "Artykuł" : "Źródło"}</th>
              <th className="text-right font-semibold pb-2">Wejścia</th>
              <th className="text-right font-semibold pb-2">Zamówienia</th>
              <th className="text-right font-semibold pb-2">Opłacone</th>
              <th className="text-right font-semibold pb-2">Wpłaty</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.source} data-testid={`${testId}-row`} className="border-t border-border/60">
                <td className="py-2 pr-3">
                  <span className="font-medium">{article ? r.label.replace(/^Artykuł: /, "") : r.label}</span>
                  {r.conversion !== null && r.conversion !== undefined && <span className="ml-2 text-[11px] text-muted-foreground">konwersja {r.conversion}%</span>}
                </td>
                <td className="py-2 text-right font-mono">{r.opens}</td>
                <td className="py-2 text-right font-mono font-semibold">{r.orders}</td>
                <td className="py-2 text-right font-mono text-brand-teal">{r.paid}</td>
                <td className="py-2 text-right font-mono whitespace-nowrap">{fmtPln(r.revenue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
